/**
 * Two-factor (MFA) state of a Supabase session.
 *
 * Every user has an authenticator app. After a password sign-in Supabase
 * issues an `aal1` session; verifying a TOTP code upgrades it to `aal2`. A
 * passkey sign-in is also `aal1` (with a `passkey` entry in the token's `amr`
 * claim) but needs no code: the passkey replaces both. These helpers only
 * decide which UI step to show — the API applies the same rule independently
 * (luminaria-api/src/plugins/auth.ts), so nothing here is a security boundary.
 */

import type { Session } from '@supabase/supabase-js';

/** What a first-factor-only session still needs: set up an authenticator, or enter its code. */
export type MfaStep = 'enroll' | 'challenge';

type SessionLike = Pick<Session, 'access_token' | 'user'>;

const FULLY_VERIFIED_AAL = 'aal2';
/** Supabase's `amr` method for a passkey sign-in. */
const PASSKEY_AMR_METHOD = 'passkey';
/**
 * `amr` methods of a session opened from an email link. A password-reset
 * link (implicit flow) is recorded as `otp`; Supabase treats all three as a
 * recovery session, which may set a new password without the current one.
 */
const EMAIL_LINK_AMR_METHODS: ReadonlySet<string> = new Set(['otp', 'magiclink', 'recovery']);
const SVG_DATA_URL_PREFIX = 'data:image/svg+xml;charset=utf-8,';
/** supabase-js prefixes the server's raw SVG with this, without URL-encoding it. */
const SDK_UNENCODED_SVG_PREFIX = 'data:image/svg+xml;utf-8,';

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** The decoded payload of a JWT access token, or null if it isn't a JWT. */
function readAccessTokenPayload(accessToken: string): Record<string, unknown> | null {
  const payloadSegment = accessToken.split('.')[1];
  if (!payloadSegment) return null;
  try {
    const payload: unknown = JSON.parse(decodeBase64Url(payloadSegment));
    return payload !== null && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** The `aal` claim of a JWT access token, or null if it has none or isn't a JWT. */
export function readAccessTokenAal(accessToken: string): string | null {
  const aal = readAccessTokenPayload(accessToken)?.aal;
  return typeof aal === 'string' ? aal : null;
}

/** An authenticator code was entered in this session (aal2). */
export function isTwoFactorSession(session: SessionLike): boolean {
  return readAccessTokenAal(session.access_token) === FULLY_VERIFIED_AAL;
}

/** How this session was signed in: the methods in Supabase's `amr` claim ([{ method, timestamp }]). */
function signInMethods(session: SessionLike): string[] {
  const amr = readAccessTokenPayload(session.access_token)?.amr;
  if (!Array.isArray(amr)) return [];
  return amr.flatMap((entry: unknown) =>
    entry !== null &&
    typeof entry === 'object' &&
    'method' in entry &&
    typeof entry.method === 'string'
      ? [entry.method]
      : []
  );
}

/** The session began with a passkey sign-in. */
export function signedInWithPasskey(session: SessionLike): boolean {
  return signInMethods(session).includes(PASSKEY_AMR_METHOD);
}

/** The session was opened from an email link (a password reset). */
export function signedInFromEmailLink(session: SessionLike): boolean {
  return signInMethods(session).some((method) => EMAIL_LINK_AMR_METHODS.has(method));
}

/** The id of the user's verified authenticator-app factor, if any. */
export function verifiedTotpFactorId(session: SessionLike): string | null {
  const factor = session.user.factors?.find(
    (f) => f.factor_type === 'totp' && f.status === 'verified'
  );
  return factor?.id ?? null;
}

/**
 * The two-factor step this session still needs, or null when it is signed in:
 * a code was entered (aal2), or the user signed in with a passkey.
 */
export function mfaStepFor(session: SessionLike): MfaStep | null {
  if (isTwoFactorSession(session) || signedInWithPasskey(session)) return null;
  return verifiedTotpFactorId(session) ? 'challenge' : 'enroll';
}

/**
 * An <img> src for the enrollment QR code. Rendering it as an image (never as
 * HTML) keeps the markup inert. supabase-js hands back the SVG markup inside
 * an unencoded data URL, where a `#` would start a fragment and truncate the
 * image, so raw markup is (re-)encoded; an already-encoded URL passes through.
 */
export function totpQrCodeSrc(qrCode: string): string {
  const markup = qrCode.startsWith(SDK_UNENCODED_SVG_PREFIX)
    ? qrCode.slice(SDK_UNENCODED_SVG_PREFIX.length)
    : qrCode;
  return markup.trimStart().startsWith('<')
    ? `${SVG_DATA_URL_PREFIX}${encodeURIComponent(markup)}`
    : qrCode;
}
